// Quill delta → DOCX exporter. Extracted from DocumentNavBar.handleDownload
// so both the doctor editor and the medstudent clinic page File menu can
// download the current document without duplicating the converter.

import { Document, HeadingLevel, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";

function getHeadingLevel(tag) {
  switch (tag) {
    case "h1":
      return HeadingLevel.HEADING_1;
    case "h2":
      return HeadingLevel.HEADING_2;
    case "h3":
      return HeadingLevel.HEADING_3;
    default:
      return HeadingLevel.HEADING_1;
  }
}

// Quill color values arrive in many shapes (hex, rgb(), oklch(), keywords).
// docx wants `HHHHHH` strings; anything else is dropped silently.
function quillColorToHex(color) {
  if (!color) return undefined;
  if (color.startsWith("#")) return color.slice(1).toUpperCase();
  const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch;
    return (
      ((1 << 24) + (parseInt(r) << 16) + (parseInt(g) << 8) + parseInt(b))
        .toString(16)
        .slice(1)
        .toUpperCase()
    );
  }
  if (
    color.startsWith("oklch") ||
    color.startsWith("hsl") ||
    color.startsWith("lab")
  ) {
    return undefined;
  }
  // Fall back to browser parsing for keywords / other formats.
  try {
    const tempDiv = document.createElement("div");
    tempDiv.style.color = color;
    document.body.appendChild(tempDiv);
    const computedColor = window.getComputedStyle(tempDiv).color;
    document.body.removeChild(tempDiv);
    const computedRgbMatch = computedColor.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)/,
    );
    if (computedRgbMatch) {
      const [, r, g, b] = computedRgbMatch;
      return (
        ((1 << 24) + (parseInt(r) << 16) + (parseInt(g) << 8) + parseInt(b))
          .toString(16)
          .slice(1)
          .toUpperCase()
      );
    }
  } catch (_) {
    /* ignore */
  }
  return undefined;
}

function quillBackgroundToShading(background) {
  if (!background || background === "transparent" || background === "#ffffff")
    return undefined;
  const hex = quillColorToHex(background);
  if (hex) return { type: "clear", fill: hex };
  return undefined;
}

function getAlignment(align) {
  if (!align) return undefined;
  const map = { left: "LEFT", center: "CENTER", right: "RIGHT", justify: "JUSTIFIED" };
  return map[align.toLowerCase()] || undefined;
}

// Quill delta → array of `docx` Paragraphs. Walks ops in order, accumulates
// TextRuns into the current paragraph, flushes on every `\n` insert with
// the newline's attributes applied as paragraph-level formatting.
function deltaToParagraphs(delta) {
  const out = [];
  let runs = [];
  let attrs = { header: null, align: null, indent: null };
  const lineSpacing = { line: 276, lineRule: "auto" }; // 1.15

  const flush = () => {
    const opts = {
      children: runs.length > 0 ? runs : [new TextRun({ text: "" })],
      spacing: lineSpacing,
    };
    if (attrs.header) opts.heading = getHeadingLevel(`h${attrs.header}`);
    if (attrs.align) opts.alignment = getAlignment(attrs.align);
    if (attrs.indent) opts.indent = { left: attrs.indent * 720 };
    out.push(new Paragraph(opts));
    runs = [];
    attrs = { header: null, align: null, indent: null };
  };

  const ops = delta?.ops || [];
  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    if (!op.insert) continue;
    const text = typeof op.insert === "string" ? op.insert : "";
    const opAttrs = op.attributes || {};

    if (text === "\n") {
      if (opAttrs.header !== undefined) attrs.header = opAttrs.header;
      if (opAttrs.align !== undefined) attrs.align = opAttrs.align;
      if (opAttrs.indent !== undefined) attrs.indent = opAttrs.indent;
      flush();
      continue;
    }

    if (text.length === 0) continue;

    // Look ahead for a newline op so we can apply its paragraph attrs to
    // the run we're about to push (mirrors how Quill stores formatting).
    const nextOp = ops[i + 1];
    if (nextOp && typeof nextOp.insert === "string" && nextOp.insert === "\n") {
      const na = nextOp.attributes || {};
      if (na.header !== undefined) attrs.header = na.header;
      if (na.align !== undefined) attrs.align = na.align;
      if (na.indent !== undefined) attrs.indent = na.indent;
    } else {
      if (opAttrs.header !== undefined) attrs.header = opAttrs.header;
      if (opAttrs.align !== undefined) attrs.align = opAttrs.align;
      if (opAttrs.indent !== undefined) attrs.indent = opAttrs.indent;
    }

    // Inline runs may themselves contain embedded `\n` (rare in Quill but
    // possible after a paste). Split and emit one run per segment.
    const parts = text.split("\n");
    for (let p = 0; p < parts.length; p++) {
      const part = parts[p];
      if (part.length > 0) {
        runs.push(
          new TextRun({
            text: part,
            bold: opAttrs.bold || false,
            italics: opAttrs.italic || false,
            underline: opAttrs.underline ? {} : undefined,
            strike: opAttrs.strike || false,
            color: quillColorToHex(opAttrs.color),
            shading: quillBackgroundToShading(opAttrs.background),
          }),
        );
      }
      if (p < parts.length - 1) flush();
    }
  }
  flush();
  if (out.length === 0) {
    out.push(new Paragraph({ children: [new TextRun({ text: "" })], spacing: lineSpacing }));
  }
  return out;
}

/**
 * Build a .docx from the active Quill editor and save it under `filename`.
 *
 * @param {object} editor    The Quill editor instance (call `.getContents()`).
 * @param {string} filename  Base name for the download (no extension).
 * @returns {Promise<void>}  Resolves when the file is saved.
 */
export async function exportEditorToDocx(editor, filename) {
  if (!editor || typeof editor.getContents !== "function") {
    throw new Error("exportEditorToDocx: invalid editor");
  }
  const delta = editor.getContents();
  if (!delta || !delta.ops || delta.ops.length === 0) {
    throw new Error("exportEditorToDocx: editor has no content");
  }
  const paragraphs = deltaToParagraphs(delta);
  const doc = new Document({
    sections: [{ properties: {}, children: paragraphs }],
  });
  const blob = await Packer.toBlob(doc);
  const safe = (filename || "document").replace(/[\\/:*?"<>|]+/g, "_");
  saveAs(blob, `${safe}.docx`);
}
